import { ChatMessage } from './hooks/useChatMessages';

/**
 * The two decisions `AiAssistantPage` makes before a turn goes on the wire,
 * kept out of the component so they are testable without rendering it.
 *
 * ## DASHBOARD-3X
 *
 * `POST /ai-assistant/chat` refuses a request whose history contains ANY
 * message that is empty, whitespace-only, or longer than
 * {@link MAX_MESSAGE_LENGTH} — and this page replays the whole transcript on
 * every turn. Two client-side paths used to put an unsendable entry into that
 * transcript permanently, because `addMessage` runs before the request
 * resolves and the store is what the next turn reads:
 *
 * 1. **An over-long input.** Nothing checked the length, so a long paste or a
 *    long dictation was appended and then replayed forever: the first send
 *    400s and so does every send after it, with no in-product way back. The
 *    guard below refuses it BEFORE it enters the transcript, and deliberately
 *    leaves the text in the composer so the operator can shorten it.
 * 2. **The page's own error line.** A failed turn appends
 *    `{ role: 'assistant', content: 'Error: …' }`, which was then sent back to
 *    the API — and to the model — as though the assistant had said it.
 *    {@link historyForWire} drops those, so a failure cannot change what the
 *    next turn replays.
 */

/**
 * Mirrors `MAX_MESSAGE_LENGTH` in `api/src/ai-assistant/message-limits.ts`,
 * which is the authority — there is no shared module across the two projects.
 *
 * The cost of drift is bounded on purpose: if the API's bound ever drops below
 * this one, the operator is back to seeing the server's raw refusal, but the
 * conversation can no longer be wedged by it, because the API-side normalizer
 * added for the same issue closes that independently of this guard.
 */
export const MAX_MESSAGE_LENGTH = 4000;

export type SendRejection = 'empty' | 'too-long';

/**
 * Why this input cannot be sent, or `null` when it can.
 *
 * Measures the TRIMMED text for emptiness (the server's own test is
 * `content.trim().length === 0`) and the trimmed text for length too, since
 * the trimmed value is what gets sent.
 */
export function rejectionForInput(raw: string): SendRejection | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'empty';
  if (trimmed.length > MAX_MESSAGE_LENGTH) return 'too-long';
  return null;
}

/**
 * The history to send, with the page's locally-generated error lines removed.
 *
 * Every other field is preserved — `actions` included, because for a NEW
 * conversation the API persists the client-supplied history verbatim, so
 * stripping it would silently drop the action buttons from a reloaded
 * transcript.
 */
export function historyForWire(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter((message) => !message.isLocalError);
}
